import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { api } from "../../lib/api";
import { toast } from "sonner";
import AttachmentsPanel from "./AttachmentsPanel";

/**
 * Reusable Entry modal.
 * props.fields: [{ name, label, type: "text"|"textarea"|"select"|"number"|"date"|"time", options?: [] }]
 * props.entry: existing entry or null
 * props.onSave(payload) async
 * props.onDelete?()
 */
export default function EntryModal({
  open,
  onOpenChange,
  title,
  fields = [],
  entry = null,
  defaults = {},
  onSave,
  onDelete,
  promptAddContact = true,
  attachmentsTargetKind = null, // "contact" | "category" | "event" — enables document uploads when set
}) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState({ open: false, name: "", resolve: null });
  const [similar, setSimilar] = useState({ open: false, name: "", candidates: [], resolve: null });

  useEffect(() => {
    if (open) {
      setForm({
        status: "Not Started",
        priority: "Normal",
        ...defaults,
        ...(entry || {}),
      });
    }
    // eslint-disable-next-line
  }, [open, entry]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /**
   * When the user blurs the name field, look up the contact and auto-fill
   * email + phone + company + contact_id if those are blank in the form.
   * Never overwrites values the user already typed.
   */
  const autofillFromMatch = async (rawName) => {
    const candidate = (rawName || "").trim();
    if (!candidate || candidate.split(/\s+/).length < 2) return;
    const [first_name, ...rest] = candidate.split(/\s+/);
    const last_name = rest.join(" ");
    try {
      const { data: match } = await api.post("/contacts/match", {
        first_name,
        last_name,
      });
      if (!match) return;
      setForm((f) => {
        const next = { ...f };
        if (!next.contact_id) next.contact_id = match.id;
        if (!(next.email || "").trim() && match.email) next.email = match.email;
        if (!(next.phone || "").trim() && match.phone) next.phone = match.phone;
        if (!(next.company || "").trim() && match.company) next.company = match.company;
        return next;
      });
      const filled = [];
      if (match.email && !form.email) filled.push("email");
      if (match.phone && !form.phone) filled.push("phone");
      if (match.company && !form.company) filled.push("company");
      if (filled.length) {
        toast.success(
          `Autofilled ${filled.join(", ")} from ${match.first_name || match.name || ""} ${match.last_name || ""}`.trim()
        );
      }
    } catch (e) {
      // silent; autofill is best-effort
    }
  };

  const askContactType = (name) =>
    new Promise((resolve) => setPicker({ open: true, name, resolve }));

  const pickerChoose = (type) => {
    if (picker.resolve) picker.resolve(type);
    setPicker({ open: false, name: "", resolve: null });
  };

  // Returns one of: { action: "use", contact }, { action: "add" }, { action: "skip" }
  const askSimilarResolution = (name, candidates) =>
    new Promise((resolve) => setSimilar({ open: true, name, candidates, resolve }));

  const similarChoose = (result) => {
    if (similar.resolve) similar.resolve(result);
    setSimilar({ open: false, name: "", candidates: [], resolve: null });
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      // optional auto-link to contacts — scan multiple candidate fields
      if (promptAddContact) {
        const company = (form.company || "").trim();
        const email = (form.email || "").trim();
        const phone = (form.phone || "").trim();

        // collect every plausible person-name string on the form
        const NAME_KEYS = [
          "individual",
          "name",
          "borrowers",
          "referred_by",
          "referred_individual",
          "introduced_individual",
          "introduced_name",
        ];
        const candidates = [];
        for (const k of NAME_KEYS) {
          const v = (form[k] || "").trim();
          if (v && v.includes(" ")) candidates.push(v);
        }
        // company can sometimes hold a person's name (Pipeline "Company / Individual(s)")
        if (
          candidates.length === 0 &&
          company &&
          company.includes(" ") &&
          !/\b(Inc|LLC|Corp|Co|Ltd|LLP|Group|Realty|Capital|Partners|Bank|Holdings)\b/i.test(company)
        ) {
          candidates.push(company);
        }
        // de-dup
        const unique = [...new Set(candidates)];

        for (const fullName of unique) {
          const parts = fullName.split(/\s+/);
          const first_name = parts[0] || "";
          const last_name = parts.slice(1).join(" ") || "";
          if (!first_name || !last_name) continue;
          try {
            const { data: match } = await api.post("/contacts/match", {
              first_name,
              last_name,
              email,
              phone,
              company,
            });
            if (match) {
              form.contact_id = form.contact_id || match.id;
              continue; // try next candidate; existing contacts skip prompt
            }
            // No exact match — check for SIMILAR contacts (typo/variant guard)
            let similarSkip = false;
            try {
              const { data: sim } = await api.post("/contacts/similar", {
                first_name,
                last_name,
                email,
                phone,
                company,
              });
              if (Array.isArray(sim) && sim.length > 0) {
                const decision = await askSimilarResolution(fullName, sim);
                if (!decision || decision.action === "skip") {
                  similarSkip = true;
                } else if (decision.action === "use" && decision.contact) {
                  form.contact_id = form.contact_id || decision.contact.id;
                  toast.success(`Linked to existing contact: ${decision.contact.first_name || decision.contact.name || ""} ${decision.contact.last_name || ""}`.trim());
                  similarSkip = true;
                }
                // action === "add" -> fall through to type picker below
              }
            } catch (e) {
              // network/best-effort
            }
            if (similarSkip) continue;
            const type = await askContactType(fullName);
            if (type) {
              const payload =
                type === "linkedin"
                  ? {
                      type: "linkedin",
                      name: fullName,
                      company,
                      notes: form.notes || "",
                      tags: [],
                    }
                  : {
                      type,
                      first_name,
                      last_name,
                      email,
                      phone,
                      company,
                      notes: form.notes || "",
                      tags: [],
                    };
              const { data: created } = await api.post("/contacts", payload);
              form.contact_id = form.contact_id || created.id;
              const labels = { coi: "Centers of Influence", prospect: "Prospects", linkedin: "LinkedIn" };
              toast.success(`Contact added to ${labels[type]}`);
            }
          } catch (e) {
            // best-effort, ignore
          }
        }
      }
      await onSave(form);
      onOpenChange(false);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object") {
        toast.error(detail.message || "Duplicate contact — save cancelled.");
      } else {
        toast.error(detail || e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="entry-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {fields.map((f) => (
            <div key={f.name} className={f.full ? "col-span-2" : "col-span-1"}>
              <label className="label-eyebrow block mb-1">{f.label}</label>
              {f.type === "textarea" ? (
                <Textarea
                  value={form[f.name] || ""}
                  onChange={(e) => setField(f.name, e.target.value)}
                  rows={3}
                  data-testid={`field-${f.name}`}
                />
              ) : f.type === "select" ? (
                <Select value={form[f.name] || ""} onValueChange={(v) => setField(f.name, v)}>
                  <SelectTrigger data-testid={`field-${f.name}`}>
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {(f.options || []).map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "multiselect" ? (
                <div className="flex flex-wrap gap-2 p-2 border border-[var(--rw-border)] rounded-[4px] bg-white" data-testid={`field-${f.name}`}>
                  {(f.options || []).map((o) => {
                    const current = Array.isArray(form[f.name]) ? form[f.name] : [];
                    const active = current.includes(o);
                    return (
                      <button
                        type="button"
                        key={o}
                        onClick={() =>
                          setField(
                            f.name,
                            active ? current.filter((x) => x !== o) : [...current, o]
                          )
                        }
                        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                          active
                            ? "bg-[var(--rw-sidebar)] text-white border-transparent"
                            : "bg-white text-neutral-700 border-[var(--rw-border)]"
                        }`}
                      >
                        {o}
                      </button>
                    );
                  })}
                  {(!f.options || f.options.length === 0) && (
                    <span className="text-xs text-neutral-400">No options configured.</span>
                  )}
                </div>
              ) : f.type === "file" ? (
                <div>
                  <input
                    type="file"
                    accept={f.accept || "*"}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append("file", file);
                      try {
                        const { data } = await api.post(f.uploadTo || "/receipts", fd, {
                          headers: { "Content-Type": "multipart/form-data" },
                        });
                        setField(f.name, data.id);
                        setField(f.name + "_filename", data.filename);
                        toast.success("Uploaded");
                      } catch (err) {
                        toast.error("Upload failed");
                      }
                    }}
                    className="block w-full text-xs border border-[var(--rw-border)] rounded-[4px] p-2"
                    data-testid={`field-${f.name}`}
                  />
                  {form[f.name] && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/receipts/${form[f.name]}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block text-xs underline mt-1 text-neutral-700"
                    >
                      View {form[f.name + "_filename"] || "file"}
                    </a>
                  )}
                </div>
              ) : (
                <Input
                  type={f.type || "text"}
                  value={form[f.name] ?? ""}
                  onChange={(e) =>
                    setField(f.name, f.type === "number" ? parseFloat(e.target.value || 0) : e.target.value)
                  }
                  onBlur={
                    ["individual", "name", "borrowers"].includes(f.name)
                      ? () => autofillFromMatch(form[f.name])
                      : undefined
                  }
                  data-testid={`field-${f.name}`}
                />
              )}
            </div>
          ))}
        </div>
        {attachmentsTargetKind && (
          <div className="mt-3">
            <AttachmentsPanel
              targetKind={attachmentsTargetKind}
              targetId={entry?.id}
              title="Documents"
            />
          </div>
        )}
        <DialogFooter className="flex justify-between sm:justify-between gap-2">
          <div className="flex gap-2">
            {onDelete && entry?.id && (
              <Button
                type="button"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={async () => {
                  if (window.confirm("Delete this entry?")) {
                    await onDelete();
                    onOpenChange(false);
                  }
                }}
                data-testid="entry-delete"
              >
                Delete
              </Button>
            )}
            {promptAddContact && (
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  const candidate =
                    (form.individual || form.name || form.borrowers || form.company || "").trim();
                  if (!candidate) {
                    toast.error("Enter a name first.");
                    return;
                  }
                  const parts = candidate.split(/\s+/);
                  const first_name = parts[0] || "";
                  const last_name = parts.slice(1).join(" ") || "";
                  if (!last_name) {
                    toast.error("Enter both first and last name.");
                    return;
                  }
                  // Check for similar contacts first
                  try {
                    const { data: sim } = await api.post("/contacts/similar", {
                      first_name,
                      last_name,
                      email: form.email || "",
                      phone: form.phone || "",
                      company: form.company || "",
                    });
                    if (Array.isArray(sim) && sim.length > 0) {
                      const decision = await askSimilarResolution(candidate, sim);
                      if (!decision || decision.action === "skip") return;
                      if (decision.action === "use" && decision.contact) {
                        setForm((f) => ({ ...f, contact_id: decision.contact.id }));
                        toast.success("Linked to existing contact");
                        return;
                      }
                    }
                  } catch (e) {
                    // best-effort
                  }
                  const type = await askContactType(candidate);
                  if (!type) return;
                  try {
                    const payload =
                      type === "linkedin"
                        ? { type, name: candidate, company: form.company || "", notes: form.notes || "", tags: [] }
                        : {
                            type,
                            first_name,
                            last_name,
                            email: form.email || "",
                            phone: form.phone || "",
                            company: form.company || "",
                            notes: form.notes || "",
                            tags: [],
                          };
                    const { data: created } = await api.post("/contacts", payload);
                    setForm((f) => ({ ...f, contact_id: created.id }));
                    const labels = { coi: "Centers of Influence", prospect: "Prospects", linkedin: "LinkedIn" };
                    toast.success(`Added to ${labels[type]}`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || err.message);
                  }
                }}
                data-testid="entry-add-contact"
              >
                + Add to Contacts
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="entry-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={busy}
              className="bg-[var(--rw-sidebar)] hover:bg-[var(--rw-sidebar-hover)]"
              data-testid="entry-save"
            >
              {busy ? "Saving…" : "Save Entry"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      <Dialog open={picker.open} onOpenChange={(v) => !v && pickerChoose(null)}>
        <DialogContent className="max-w-md bg-white z-[100]" data-testid="contact-type-picker">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add to Contacts?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-600">
            <span className="font-semibold">{picker.name}</span> isn't in your contacts yet. Which tab should they live in?
          </p>
          <div className="grid grid-cols-1 gap-2 mt-3">
            <button
              type="button"
              onClick={() => pickerChoose("coi")}
              className="flex items-center gap-3 border border-[var(--rw-border)] rounded-[4px] px-4 py-3 text-left hover:bg-[var(--rw-surface-2)]"
              data-testid="pick-coi"
            >
              <span className="nav-dot" style={{ background: "#E54D2E" }} />
              <div>
                <div className="font-semibold">Centers of Influence</div>
                <div className="text-xs text-neutral-500">Attorneys, CPAs, lenders, realtors, referral partners.</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => pickerChoose("prospect")}
              className="flex items-center gap-3 border border-[var(--rw-border)] rounded-[4px] px-4 py-3 text-left hover:bg-[var(--rw-surface-2)]"
              data-testid="pick-prospect"
            >
              <span className="nav-dot" style={{ background: "#F76B15" }} />
              <div>
                <div className="font-semibold">Prospects</div>
                <div className="text-xs text-neutral-500">Potential borrowers and active opportunities.</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => pickerChoose("linkedin")}
              className="flex items-center gap-3 border border-[var(--rw-border)] rounded-[4px] px-4 py-3 text-left hover:bg-[var(--rw-surface-2)]"
              data-testid="pick-linkedin"
            >
              <span className="nav-dot" style={{ background: "#0067B8" }} />
              <div>
                <div className="font-semibold">LinkedIn</div>
                <div className="text-xs text-neutral-500">A LinkedIn connection — minimal fields.</div>
              </div>
            </button>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => pickerChoose(null)} data-testid="pick-skip">
              Don't add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={similar.open} onOpenChange={(v) => !v && similarChoose({ action: "skip" })}>
        <DialogContent className="max-w-lg bg-white z-[100]" data-testid="similar-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              A similar contact already exists
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-600">
            You're about to add <span className="font-semibold">{similar.name}</span>, but we found
            {similar.candidates.length === 1 ? " a similar contact " : ` ${similar.candidates.length} similar contacts `}
            already in your list. Pick one to link to it, or continue adding the new one anyway.
          </p>
          <div className="grid grid-cols-1 gap-2 mt-3 max-h-[40vh] overflow-y-auto">
            {similar.candidates.map((c) => {
              const label =
                (c.first_name || c.last_name)
                  ? `${c.first_name || ""} ${c.last_name || ""}`.trim()
                  : (c.name || "(unnamed)");
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => similarChoose({ action: "use", contact: c })}
                  className="flex flex-col text-left border border-[var(--rw-border)] rounded-[4px] px-3 py-2 hover:bg-[var(--rw-surface-2)]"
                  data-testid={`similar-use-${c.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{label}</div>
                    <span className="text-[10px] uppercase tracking-[0.08em] text-neutral-500">
                      {c.type === "coi" ? "COI" : c.type === "linkedin" ? "LinkedIn" : "Prospect"}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-600 mt-0.5">
                    {[c.company, c.email, c.phone].filter(Boolean).join(" · ") || "—"}
                  </div>
                  {(c._reasons || []).length > 0 && (
                    <div className="text-[11px] text-neutral-500 italic mt-1">
                      Matched: {c._reasons.join(", ")}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <DialogFooter className="flex justify-between sm:justify-between gap-2 mt-2">
            <Button
              variant="ghost"
              onClick={() => similarChoose({ action: "skip" })}
              data-testid="similar-skip"
            >
              Skip — don't link
            </Button>
            <Button
              variant="outline"
              onClick={() => similarChoose({ action: "add" })}
              data-testid="similar-add-anyway"
            >
              Add as new contact anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
